import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EncryptionService } from '../Encryption/encryption.service';
import { MatDialog } from '@angular/material/dialog';
import { InfoDialogComponent } from './info-dialog-component/info-dialog-component.component';

@Injectable({
  providedIn: 'root',
})
export class RoleAccessGuard implements CanActivate {
  orgId: string = null;

  constructor(
    private router: Router,
    private encryptionService: EncryptionService,
    private dialog: MatDialog
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.encryptionService.currentUser$.pipe(
      map((user) => {
        if (!user) {
          // Open dialog for authentication required
          this.openDialog({
            title: 'Authentication Required',
            message: 'Please log in to access this page.',
            type: 'warning',
          });
          this.router.navigate(['/authentication/login']);
          return false;
        }

        const currentPath = state.url;
        const hasAccess = this.checkPathAccess(
          currentPath,
          user.role,
          user.permissions
        );

        if (!hasAccess) {
          // Open dialog for access denied
          this.openDialog({
            title: 'Access Denied',
            message: `You don't have permission to access this page. Your role '${user.role}' is not authorized to access this feature.`,
            type: 'error',
          });
          this.router.navigate(['/dashboard']); // Redirect to a safe default route
          return false;
        }

        return true;
      })
    );
  }

  private openDialog(config: {
    title: string;
    message: string;
    type: 'warning' | 'error' | 'info';
  }) {
    // Example using Angular Material Dialog
    this.dialog.open(InfoDialogComponent, {
      width: '400px',
      data: {
        title: config.title,
        message: config.message,
        type: config.type,
      },
    });
  }
  /**
   * Check if user has access to the given path based on role and permissions
   */
  private checkPathAccess(
    currentPath: string,
    userRole: string,
    userPermissions: any[]
  ): boolean {
    if (!userPermissions || userPermissions.length === 0) {
      console.warn('No permissions found for user');
      return false;
    }

    // Find the most specific matching path permission
    const matchingPermission = this.findMatchingPathPermission(
      currentPath,
      userRole,
      userPermissions
    );

    if (matchingPermission && matchingPermission.permissions?.length > 0) {
      return true; // User has specific permissions for this path
    }

    // If no specific path permission found, deny access
    console.warn(
      `No matching permission found for path: ${currentPath}, role: ${userRole}`
    );
    return false;
  }

  /**
   * Find the most specific matching path permission from user's permissions array
   */
  private findMatchingPathPermission(
    currentPath: string,
    userRole: string,
    userPermissions: any[]
  ): any | null {
    // Filter permissions that match the user's role and where the current path starts with the permission path
    const matchingPermissions = userPermissions.filter(
      (p) => p.role === userRole && currentPath.startsWith(p.path)
    );

    if (matchingPermissions.length === 0) {
      return null;
    }

    // Sort by path length descending to get the most specific match
    // For example: if we have both '/dashboard' and '/dashboard/analytics',
    // and current path is '/dashboard/analytics/reports', we want '/dashboard/analytics'
    const sortedPermissions = matchingPermissions.sort(
      (a, b) => b.path.length - a.path.length
    );

    return sortedPermissions[0];
  }

  /**
   * Check if user has specific permission for a path (can be used in components)
   */
  hasPermissionForPath(path: string, permission: string): boolean {
    const user = this.encryptionService.getCurrentUser();
    if (!user || !user.permissions) return false;
    console.log(user.role, user.permissions);

    const matchingPermission = this.findMatchingPathPermission(
      path,
      user.role,
      user.permissions
    );

    if (matchingPermission) {
      return matchingPermission.permissions.includes(permission);
    }

    return false;
  }

  /**
   * Get user's permissions for a specific path
   */
  getPermissionsForPath(path: string): string[] {
    const user = this.encryptionService.getCurrentUser();
    if (!user || !user.permissions) return [];

    const matchingPermission = this.findMatchingPathPermission(
      path,
      user.role,
      user.permissions
    );

    return matchingPermission ? matchingPermission.permissions || [] : [];
  }

  /**
   * Get all permissions for current user (useful for debugging)
   */
  getAllUserPermissions(): any[] {
    const user = this.encryptionService.getCurrentUser();
    return user?.permissions || [];
  }

  /**
   * Check if user has access to any path (useful for navigation menus)
   */
  hasAccessToAnyPath(paths: string[]): boolean {
    const user = this.encryptionService.getCurrentUser();
    if (!user || !user.permissions) return false;

    return paths.some((path) => {
      const matchingPermission = this.findMatchingPathPermission(
        path,
        user.role,
        user.permissions
      );
      return matchingPermission && matchingPermission.permissions?.length > 0;
    });
  }
}
